import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import BASE_API_URL from '../../apis/index';
import CompanyCard from '../../components/CompanyCard';
import Divider from '@mui/material/Divider';
import Slider from 'react-slick';

export default function CompSearchBar({ defaultFetch = false }) {
    const [compData, setCompData] = useState([]);
    const [search, setSearch] = useState("");
    const [filterType, setFilterType] = useState("All");
    const [loading, setLoading] = useState(false);

    const fetchCompanies = async (query = "", type = "All") => {
        try {
            setLoading(true);
            const res = await axios.get(`${BASE_API_URL}/api/buyer/search`, {
                params: { search: query, type }
            });
            setCompData(res.data || []);
        } catch (err) {
            console.error("Error fetching companies:", err);
            setCompData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (defaultFetch) fetchCompanies("", "All");
    }, [defaultFetch]);

    const handleSearch = () => {
        fetchCompanies(search.trim(), filterType);
    };

    // Slider settings ---
    const settings = {
      dots: true,
      infinite: compData.length > 4,
      speed: 500,
      slidesToShow: 4,
      slidesToScroll: 1,
      arrows: true,
      responsive: [
        {
          breakpoint: 1400,
          settings: { slidesToShow: 3 },
        },
        {
          breakpoint: 1024,
          settings: { slidesToShow: 2 },
        },
        {
          breakpoint: 768,
          settings: { slidesToShow: 1 },
        },
      ],
    };

    return (
        <Wrapper>
            <SearchBarRow>
                <div className='d-flex SearchBar'>
                    <SearchInput
                        type="text"
                        placeholder="Search for Company / Product / Service"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />

                    <Divider orientation="vertical" variant="middle" flexItem />

                    <FilterSelect
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                    >
                        <option value="All">All</option>
                        <option value="Companies">Companies</option>
                        <option value="Products">Products</option>
                        <option value="Services">Services</option>
                    </FilterSelect>

                    <SearchBtn onClick={handleSearch}>Search</SearchBtn>
                </div>
            </SearchBarRow>

            {loading ? (
                <Loader>Loading...</Loader>
            ) : compData.length > 0 ? (
                <>
                    {/* <CardsGrid>
                        {compData.slice(0, 6).map((c, i) => (
                            <AnimatedCard key={i}>
                                <CompanyCard compData={c} linkType="public" />
                            </AnimatedCard>
                        ))}
                    </CardsGrid> */}
                    <SliderWrapper>
                        <Slider {...settings}>
                            {compData.slice(0, 6).map((c, i) => (
                                <SlideItem key={i}>
                                    <AnimatedCard>
                                        <CompanyCard compData={c} linkType="public" />
                                    </AnimatedCard>
                                </SlideItem>
                            ))}
                        </Slider>
                    </SliderWrapper>
                </>
            ) : (
                !defaultFetch && search === "" ? (
                    <p style={{ textAlign: "center", marginTop: "40px", color: "#666" }}>
                        🔎 Enter a company / product / service to search
                    </p>
                ) : (
                    <p style={{ textAlign: "center", gridColumn: "1 / -1" }}>
                        No results found {search && `for "${search}"`} in {filterType}.
                    </p>
                )
            )}
        </Wrapper>
    );
}


// ========================= Styled Components =========================
const Wrapper = styled.div`
.slick-slide > div{
    width: 100%;
}
.slick-arrow{
    border: 2px solid rgba(0, 0, 0, 0.2);
    width: 50px;
    height: 50px;
    background: white;
    z-index:50;
    box-shadow: 0 0 50px white;
    border-radius: 5px;

    &:before{
        color: black;
    }
}
.slick-list{
    z-index: 10;
}
.slick-track {
  display: flex !important;
}

.slick-slide {
  height: inherit !important;
  display: flex !important;
}

.slick-slide > div {
  display: flex;
  height: 100%;
}
`;

const SlideItem = styled.div`
  padding: 0 12px;
`;

const SliderWrapper = styled.div`
  max-width: 1500px;
  margin: 0 auto;
  padding: 30px 0;
`;

const Button = styled.button`
  padding: 8px 24px;
  border-radius: 50px;
  border: none;
  font-weight: 400;
  margin-left: 12px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: linear-gradient(225deg, var(--primaryLight-color) 0%, var(--secondaryLight-color) 100%);
  }
`;

const SearchBarRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 36px;
  div.SearchBar{
    box-shadow: 0 25px 60px rgba(113,106,147,.2);
    padding: 10px;
    border-radius: 10px;
    width: auto;
  }
`;

const SearchInput = styled.input`
  width: 450px;
  padding: 10.5px 20px;
  border-radius: 5px 0 0 5px;
  color: var(--primary-color);
  border: none !important;
  font-size: 1rem;
  outline: none;
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid white !important;
  font-size: 1rem;
  color: var(--primary-color);
  background: var(--white-color);
  outline: none;
  margin-right: 20px;
`;

const SearchBtn = styled(Button)`
  background: linear-gradient(90deg, var(--secondary-color) 0%, var(--primary-color) 100%);
  color: var(--white-color);
  padding: 12px 32px;
  transition: 0.2s;
  border-radius: 5px;
  margin-left: 0;
  &:hover {
    color: var(--primary-color);
  }
`;

// const CardsGrid = styled.div`
//   display: flex;
//   justify-content: center;
//   flex-wrap: wrap;
//   gap: 15px;
// `;

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
`;

const AnimatedCard = styled.div`
    animation: ${fadeIn} 0.4s ease-in-out;
`;

const Loader = styled.div`
    text-align: center;
    padding: 20px;
    font-size: 18px;
    font-weight: bold;
`;
